import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { locale, addLocale } from 'primereact/api';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './ReportesAdmin.css';

addLocale('es', {
  firstDayOfWeek: 1,
  dayNames: ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'],
  dayNamesShort: ['dom', 'lun', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  dayNamesMin: ['D', 'L', 'M', 'J', 'V', 'S'],
  monthNames: ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'],
  monthNamesShort: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'],
  today: 'Hoy',
  clear: 'Limpiar',
  weekHeader: 'Sm',
  dateFormat: 'dd/mm/yy',
  emptyMessage: 'No se encontraron registros',
  filterPlaceholder: 'Buscar',
  choose: 'Elegir',
  upload: 'Subir',
  cancel: 'Cancelar',
});

locale('es');

const ReportesAdmin = () => {
  const [canjeData, setCanjeData] = useState([]);
  const [puntosData, setPuntosData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedPuntosItems, setSelectedPuntosItems] = useState([]);
  const [activeReport, setActiveReport] = useState('canje');
  const [isAceptarCanjeDisabled, setIsAceptarCanjeDisabled] = useState(false);
  const [lazyState, setLazyState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {
      nombre: { value: '', matchMode: 'contains' },
      premio: { value: '', matchMode: 'contains' },
      descripcionPremio: { value: '', matchMode: 'contains' },
      puntos: { value: null, matchMode: 'equals' },
      fecha: { value: null, matchMode: 'equals' },
      canje_confirmado: { value: null, matchMode: 'equals' },
      fechaCanjeConfirmado: { value: null, matchMode: 'equals' }
    }
  });
  const [lazyPuntosState, setLazyPuntosState] = useState({
    first: 0,
    rows: 10,
    page: 1,
    sortField: null,
    sortOrder: null,
    filters: {
      nombre: { value: '', matchMode: 'contains' },
      puntosPositivos: { value: null, matchMode: 'equals' },
      puntosNegativos: { value: null, matchMode: 'equals' },
      fecha: { value: null, matchMode: 'equals' }
    }
  });
  const [filteredData, setFilteredData] = useState([]);
  const [filteredPuntosData, setFilteredPuntosData] = useState([]);
  const toast = useRef(null);

  useEffect(() => {
    fetchCanje();
    fetchPuntos();
  }, []);

  const fetchCanje = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/puntuacion/canjepremio/findall');
      const data = await response.json();

      const formattedData = data.map(item => ({
        id: item.id,
        idPremio: item.premio.id,
        cedulaEmpleado: item.empleado.cedula,
        nombre: item.empleado.nombre,
        premio: item.premio.nombre,
        descripcionPremio: item.premio.descripcion,
        puntos: item.premio.puntos,
        fecha: new Date(item.fecha),
        canje_confirmado: item.canje_confirmado === 'Confirmado',
        fechaCanjeConfirmado: item.fechaCanjeConfirmado ? new Date(item.fechaCanjeConfirmado) : null
      }));

      setCanjeData(formattedData);
      setFilteredData(formattedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching canje:', error);
      setLoading(false);
    }
  };

  const fetchPuntos = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/puntuacion/findall');
      const data = await response.json();

      const formattedData = data.map(item => ({
        id: item.id,
        nombre: item.empleado.nombre,
        puntosPositivos: item.categoria.puntos > 0 ? item.categoria.puntos : 0,
        puntosNegativos: item.categoria.puntos < 0 ? item.categoria.puntos : 0,
        fecha: new Date(item.fecha)
      }));

      setPuntosData(formattedData);
      setFilteredPuntosData(formattedData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching puntos:', error);
      setLoading(false);
    }
  };

  const handleEnviarCorreo = async (destinatarios, asunto, cuerpo) => {
    try {
      const correoData = {
        destinatarios: destinatarios,
        asunto: asunto,
        cuerpo: cuerpo
      };

      const response = await fetch('/email/enviar-correo', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(correoData)
      });

      if (!response.ok) {
        throw new Error('Error al enviar el correo');
      }

      // Mostrar mensaje de éxito al usuario
      toast.current.show({ severity: 'success', summary: 'Correo enviado', detail: 'El correo se envió correctamente' });

    } catch (error) {
      console.error('Error al enviar el correo:', error);
      // Mostrar mensaje de error al usuario
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo enviar el correo' });
    }
  };


  const handleAceptarCanje = async () => {
    setIsAceptarCanjeDisabled(true); // Deshabilita el botón al inicio de la función
    const currentDate = new Date(); // Obtiene la fecha actual
    const selectedItemsData = selectedItems.map(item => ({
      id: item.id,
      idPremio: item.idPremio,
      fecha: item.fecha,
      cedulaEmpleado: item.cedulaEmpleado,
      canje_confirmado: true,
      fechaCanjeConfirmado: currentDate // Añade la fecha actual
    }));

    try {
      const response = await fetch('/api/puntuacion/canjepremio/aceptarCanje', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedItemsData)
      });

      if (!response.ok) {
        throw new Error('Error aceptando canje');
      }

      // Envío de correos a los empleados cuyo canje ha sido aceptado
      for (const item of selectedItems) {
        try {
          // Obtener el correo electrónico del empleado utilizando la cédula
          const empleadoResponse = await fetch(`/api/empleado/findbyid/${item.cedulaEmpleado}`);
          if (!empleadoResponse.ok) {
            throw new Error(`Error al obtener datos del empleado con cédula: ${item.cedulaEmpleado}`);
          }
          const empleadoData = await empleadoResponse.json();
          const destinatarios = [empleadoData.correo];

          const asunto = 'Confirmación de Canje';
          const cuerpo = `Hola ${item.nombre},\n\nTu canje realizado el ${item.fecha.toLocaleDateString('es-ES')} ha sido Aceptado.\n\nSaludos,\nEquipo de Administración`;

          await handleEnviarCorreo(destinatarios, asunto, cuerpo);
        } catch (error) {
          console.error(`Error al enviar correo a ${item.nombre} (${item.cedulaEmpleado}):`, error);
        }
      }

      // Actualiza la data en el frontend después de aceptar el canje
    const updatedCanjeData = canjeData.map(item => {
      if (selectedItems.some(selectedItem => selectedItem.id === item.id)) {
        return { ...item, canje_confirmado: true, fechaCanjeConfirmado: currentDate };
      }
      return item;
    });

    setCanjeData(updatedCanjeData);
    setFilteredData(updatedCanjeData);
    setSelectedItems([]);

      // Mostrar mensaje emergente
      toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Canje aceptado exitosamente', life: 3000 });
    } catch (error) {
      console.error('Error aceptando canje:', error);
      toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo aceptar el canje' });
    } finally {
      setIsAceptarCanjeDisabled(false); // Habilita el botón nuevamente al finalizar
    }
  };

  const handleCanjeCheck = (id) => {
    const updatedCanjeData = canjeData.map(item => {
      if (item.id === id) {
        return { ...item, canje_confirmado: !item.canje_confirmado };
      }
      return item;
    });
    setCanjeData(updatedCanjeData);
    setFilteredData(updatedCanjeData);
  };

  const canjeCheckTemplate = (rowData) => {
    return (
      <input
        type="checkbox"
        checked={rowData.canje_confirmado}
        onChange={() => handleCanjeCheck(rowData.id)}
        disabled={rowData.canje_confirmado}
      />
    );
  };

  const onFilterChange = (e, isCanjeTable) => {
    const filters = e.filters;
    if (isCanjeTable) {
      setLazyState(prevState => ({ ...prevState, filters, first: 0 }));
      const filteredData = filterData(filters, canjeData);
      setFilteredData(filteredData);
    } else {
      setLazyPuntosState(prevState => ({ ...prevState, filters, first: 0 }));
      const filteredData = filterData(filters, puntosData);
      setFilteredPuntosData(filteredData);
    }
  };

  const filterData = (filters, data) => {
    let _filteredData = [...data];

    Object.keys(filters).forEach(key => {
      const { value } = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        _filteredData = _filteredData.filter(item => {
          const fieldValue = String(item[key]).toLowerCase();
          const filterValue = String(value).toLowerCase();
          return fieldValue.includes(filterValue);
        });
      }
    });

    return _filteredData;
  };


  const onPage = (event, isCanjeTable) => {
    if (isCanjeTable) {
      setLazyState(prevState => ({
        ...prevState,
        first: event.first,
        rows: event.rows
      }));
    } else {
      setLazyPuntosState(prevState => ({
        ...prevState,
        first: event.first,
        rows: event.rows
      }));
    }
  };


  const onSort = (event, isCanjeTable) => {
    if (isCanjeTable) {
      setLazyState((prevState) => ({ ...prevState, ...event, first: 0 }));
      const sortedData = sortData(event.sortField, event.sortOrder, filteredData);
      setFilteredData(sortedData);
    } else {
      setLazyPuntosState((prevState) => ({ ...prevState, ...event, first: 0 }));
      const sortedData = sortData(event.sortField, event.sortOrder, filteredPuntosData);
      setFilteredPuntosData(sortedData);
    }
  };

  const sortData = (sortField, sortOrder, data) => {
    let _sortedData = [...data];

    if (sortField && sortOrder) {
      _sortedData = _sortedData.sort((data1, data2) => {
        const value1 = data1[sortField];
        const value2 = data2[sortField];
        let result = null;

        if (typeof value1 === 'string' && typeof value2 === 'string') {
          result = value1.localeCompare(value2, 'es', { sensitivity: 'base' });
        } else {
          result = (value1 < value2) ? -1 : (value1 > value2) ? 1 : 0;
        }

        return sortOrder === 1 ? result : -result;
      });
    }

    return _sortedData;
  };

  const onSelectionChange = (e, isCanjeTable) => {
    if (isCanjeTable) {
      // Filtra los elementos seleccionados para excluir los ya confirmados
      const filteredSelection = e.value.filter(item => !item.canje_confirmado);
      setSelectedItems(filteredSelection);
    } else {
      setSelectedPuntosItems(e.value);
    }
  };

  const exportToExcel = (data) => {
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), 'Data');
    XLSX.writeFile(workbook, 'ExportedData.xlsx');
  };

  const exportToPDF = (data, isCanjeTable) => {
    const doc = new jsPDF();
    if (isCanjeTable) {
      doc.autoTable({
        head: [['Nombre', 'Premio', 'Descripción del Premio', 'Puntos', 'Fecha', 'Canje Realizado', 'Fecha de Canje Realizado']],
        body: data.map(item => ([
          item.nombre,
          item.premio,
          item.descripcionPremio,
          item.puntos,
          item.fecha.toLocaleDateString('es-ES'),
          item.canje_confirmado ? 'Sí' : 'No',
          formatDate(item.fechaCanjeConfirmado)
        ])),
      });
    } else {
      doc.autoTable({
        head: [['Nombre', 'Puntos Positivos', 'Puntos Negativos', 'Fecha']],
        body: data.map(item => ([
          item.nombre,
          item.puntosPositivos,
          item.puntosNegativos,
          item.fecha.toLocaleDateString('es-ES')
        ])),
      });
    }
    doc.save('ExportedData.pdf');
  };

  const dateBodyTemplate = (rowData) => {
    return rowData.fecha.toLocaleDateString('es-ES');
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-ES');
  };


  const renderCanjeTable = () => (
    <div className="table-container">
      <h2>Canjes de Empleados</h2>
      <Button label="Aceptar Canje" icon="pi pi-check" className="p-button-raised p-button-success" onClick={handleAceptarCanje} disabled={selectedItems.length === 0 || isAceptarCanjeDisabled} />
      <Button label="Exportar Datos Seleccionados a Excel" icon="pi pi-file-excel" className="p-button-raised p-button-success" onClick={() => exportToExcel(selectedItems)} disabled={selectedItems.length === 0} />
      <Button label="Exportar Datos Seleccionados a PDF" icon="pi pi-file-pdf" className="p-button-raised p-button-danger" onClick={() => exportToPDF(selectedItems, true)} disabled={selectedItems.length === 0} />
      <DataTable
        value={filteredData}
        lazy={false}
        filterDisplay="row"
        dataKey="id"
        paginator
        rows={lazyState.rows}
        first={lazyState.first}
        onPage={(e) => onPage(e, true)}
        onSort={(e) => onSort(e, true)}
        sortField={lazyState.sortField}
        sortOrder={lazyState.sortOrder}
        onFilter={(e) => onFilterChange(e, true)}
        filters={lazyState.filters}
        loading={loading}
        tableStyle={{ minWidth: '75rem' }}
        selection={selectedItems}
        onSelectionChange={(e) => onSelectionChange(e, true)}
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        <Column field="nombre" header="Nombre" sortable filter filterPlaceholder="Buscar" />
        <Column field="premio" header="Premio" sortable filter filterPlaceholder="Buscar" />
        <Column field="descripcionPremio" header="Descripción del Premio" sortable filter filterPlaceholder="Buscar" />
        <Column field="puntos" header="Puntos" sortable filter filterPlaceholder="Buscar" />
        <Column field="fecha" header="Fecha" sortable filter filterPlaceholder="Buscar" body={dateBodyTemplate} />
        <Column field="canje_confirmado" header="Canje Realizado" body={canjeCheckTemplate} />
        <Column field="fechaCanjeConfirmado" header="Fecha de Canje Realizado" sortable filter filterPlaceholder="Buscar"
          body={(rowData) => rowData.fechaCanjeConfirmado ? new Date(rowData.fechaCanjeConfirmado).toLocaleDateString('es-ES') : ''} />
      </DataTable>
    </div>
  );

  const renderPuntosTable = () => (
    <div className="table-container">
      <h2>Puntos de Empleados</h2>
      <Button label="Exportar Datos Seleccionados a Excel" icon="pi pi-file-excel" className="p-button-raised p-button-success" onClick={() => exportToExcel(selectedPuntosItems)} disabled={selectedPuntosItems.length === 0} />
      <Button label="Exportar Datos Seleccionados a PDF" icon="pi pi-file-pdf" className="p-button-raised p-button-danger" onClick={() => exportToPDF(selectedPuntosItems, false)} disabled={selectedPuntosItems.length === 0} />
      <DataTable
        value={filteredPuntosData}
        lazy={false}
        filterDisplay="row"
        dataKey="id"
        paginator
        rows={lazyPuntosState.rows}
        first={lazyPuntosState.first}
        onPage={(e) => onPage(e, false)}
        onSort={(e) => onSort(e, false)}
        sortField={lazyPuntosState.sortField}
        sortOrder={lazyPuntosState.sortOrder}
        onFilter={(e) => onFilterChange(e, false)}
        filters={lazyPuntosState.filters}
        loading={loading}
        tableStyle={{ minWidth: '75rem' }}
        selection={selectedPuntosItems}
        onSelectionChange={(e) => onSelectionChange(e, false)}
      >
        <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} />
        <Column field="nombre" header="Nombre" sortable filter filterPlaceholder="Buscar" />
        <Column field="puntosPositivos" header="Puntos Positivos" sortable filter filterPlaceholder="Buscar" />
        <Column field="puntosNegativos" header="Puntos Negativos" sortable filter filterPlaceholder="Buscar" />
        <Column field="fecha" header="Fecha" sortable filter filterPlaceholder="Buscar" body={dateBodyTemplate} />
      </DataTable>
    </div>
  );

  return (
    <div className="ReportesAdmin">
      <Toast ref={toast} />
      <h1>Reportes de Administración</h1>
      <div className="button-container">
        <Button
          label="Reporte Canje"
          icon="pi pi-list"
          className={`p-button-raised ${activeReport === 'canje' ? 'p-button-primary' : 'p-button-secondary'}`}
          onClick={() => setActiveReport('canje')}
        />
        <Button
          label="Reporte Puntos"
          icon="pi pi-chart-bar"
          className={`p-button-raised ${activeReport === 'puntos' ? 'p-button-primary' : 'p-button-secondary'}`}
          onClick={() => setActiveReport('puntos')}
        />
      </div>
      {activeReport === 'canje' ? renderCanjeTable() : renderPuntosTable()}
    </div>
  );
};

export default ReportesAdmin;